import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createApi, type Api, type TokenStore } from "../api/client";
import { refresh as apiRefresh } from "../api/auth";

interface AccountSummary {
  id: string;
  email: string;
  display_name: string;
  role: string;
}

interface AuthState {
  status: "unauthenticated" | "authenticated";
  account: AccountSummary | null;
}

interface AuthContextValue {
  state: AuthState;
  api: Api;
  setSession: (s: {
    access_token: string;
    refresh_token: string;
    account: AccountSummary;
  }) => void;
  clearSession: () => void;
}

const STORAGE_KEY = "prism-auth-session";

interface PersistedSession {
  access_token: string;
  refresh_token: string;
  account: AccountSummary;
}

function readPersisted(): PersistedSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PersistedSession) : null;
  } catch {
    return null;
  }
}

function writePersisted(s: PersistedSession | null) {
  try {
    if (s) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const p = readPersisted();
    return p ? { status: "authenticated", account: p.account } : { status: "unauthenticated", account: null };
  });

  // Live token store — mutated in place so the API client always sees the latest.
  const tokenStoreRef = useRef<TokenStore>((() => {
    const p = readPersisted();
    return { accessToken: p?.access_token ?? null, refreshToken: p?.refresh_token ?? null };
  })());

  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);

  const setSession = (s: { access_token: string; refresh_token: string; account: AccountSummary }) => {
    tokenStoreRef.current.accessToken = s.access_token;
    tokenStoreRef.current.refreshToken = s.refresh_token;
    writePersisted(s);
    setState({ status: "authenticated", account: s.account });
  };

  const clearSession = () => {
    tokenStoreRef.current.accessToken = null;
    tokenStoreRef.current.refreshToken = null;
    writePersisted(null);
    setState({ status: "unauthenticated", account: null });
  };

  const api: Api = useMemo(() => {
    const store = tokenStoreRef.current;
    // Construct a refresh function that de-dupes concurrent refreshes.
    const refreshFn = async (): Promise<boolean> => {
      if (refreshPromiseRef.current) return refreshPromiseRef.current;
      const current = store.refreshToken;
      if (!current) return false;
      const p = (async () => {
        try {
          // Use a fresh API instance without recursion; pass a no-op refresh.
          const bare = createApi(store, async () => false);
          const resp = await apiRefresh(bare, current);
          store.accessToken = resp.access_token;
          store.refreshToken = resp.refresh_token;
          const persisted = readPersisted();
          if (persisted) writePersisted({ ...persisted, access_token: resp.access_token, refresh_token: resp.refresh_token });
          return true;
        } catch {
          clearSession();
          return false;
        } finally {
          refreshPromiseRef.current = null;
        }
      })();
      refreshPromiseRef.current = p;
      return p;
    };
    return createApi(store, refreshFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync across tabs/windows.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const p = readPersisted();
      if (p) {
        tokenStoreRef.current.accessToken = p.access_token;
        tokenStoreRef.current.refreshToken = p.refresh_token;
        setState({ status: "authenticated", account: p.account });
      } else {
        tokenStoreRef.current.accessToken = null;
        tokenStoreRef.current.refreshToken = null;
        setState({ status: "unauthenticated", account: null });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <AuthContext.Provider value={{ state, api, setSession, clearSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
